import {
  APPROVE_METHOD_ID,
  checkIsERC721InputData,
  checkIsNFTInputData,
} from '@exodus/ethereum-lib'
import { asset as ethereum } from '@exodus/ethereum-meta'
import SolidityContract from '@exodus/solidity-contract'
import assert from 'minimalistic-assert'

import { getERC20Params } from '../eth-like-util.js'
import { fetchTxPreview } from './fetch-tx-preview.js'

const ethDecimals = ethereum.units.ETH

const ethHexToInt = (hexValue) => parseInt(hexValue, '16')

async function getAssetSymbolFromContract(contractAddress, asset) {
  const { symbol: assetSymbol } = await getERC20Params({
    address: contractAddress,
    asset,
    paramNames: ['symbol'],
  })

  return assetSymbol
}

// Sometimes ERC20 transfers happen with a proxy contract and Blocknative Preview API duplicates them.
// We consider transfers having the same type (ERC20), symbol, decimals number and the delta number (transfer amount)
// as duplicates and remove them.
export function maybeRemoveDuplicates(balanceChanges) {
  // We didn't encounter duplicates in other cases so doing this check to limit the function impact.
  if (balanceChanges.length !== 2) return balanceChanges

  const uniqueBalanceChanges = []
  const existingBalanceChangesSet = new Set()
  for (const balanceChange of balanceChanges) {
    const { asset: assetData, delta } = balanceChange
    if (assetData.type !== 'erc20') {
      uniqueBalanceChanges.push(balanceChange)
      continue
    }

    const key = `${assetData.type}:${assetData.symbol}:${assetData.decimal}:${delta}`
    if (existingBalanceChangesSet.has(key)) continue

    existingBalanceChangesSet.add(key)
    uniqueBalanceChanges.push(balanceChange)
  }

  return uniqueBalanceChanges
}

async function prepareBalanceChanges(
  internalTransactions,
  balanceChanges,
  transactionInput,
  asset
) {
  const assetNameToSymbolMap = Object.create(null)
  assetNameToSymbolMap['ethereum'] = 'ETH' // Refactor after blowfish integration

  const preparedBalanceChanges = [...balanceChanges]

  const decimals = Object.create(null)
  decimals[assetNameToSymbolMap[asset.name]] = asset.currency.defaultUnit.power // Always have a default asset decimal in map for edge cases

  const assetSymbols = Object.create(null)
  let contractName
  let isERC721 = false
  let isERC20 = false

  if (internalTransactions && Array.isArray(internalTransactions)) {
    for (const transaction of internalTransactions) {
      const { contractCall, input } = transaction

      if (!contractCall) continue

      const { decimalValue } = contractCall
      isERC721 =
        checkIsNFTInputData(transactionInput) || (!decimalValue && checkIsERC721InputData(input))
      isERC20 =
        !isERC721 &&
        (contractCall.methodName === 'transfer' || contractCall.methodName === 'transferFrom')

      if (isERC20) {
        const assetSymbol =
          contractCall.contractAlias ||
          (await getAssetSymbolFromContract(contractCall.contractAddress, asset))

        assetSymbols[contractCall.contractAddress] = assetSymbol
        decimals[assetSymbol] = contractCall.contractDecimals
      }

      if (isERC721) {
        contractName = contractCall.contractName
      }
    }
  }

  for (const balanceChange of preparedBalanceChanges) {
    const { asset: assetData } = balanceChange

    if (!assetData.symbol) {
      assetData.symbol =
        assetSymbols[assetData.contractAddress] ||
        (await getAssetSymbolFromContract(assetData.contractAddress, asset))
    }

    if (isERC721) {
      assetData.type = 'erc721'
      assetData.title = contractName
      continue
    }

    if (typeof decimals[assetData.symbol] === 'number') {
      assetData.decimal = decimals[assetData.symbol]
      continue
    }

    if (isERC20 && assetData.contractAddress) {
      const { decimals: assetDecimal } = await getERC20Params({
        address: assetData.contractAddress,
        asset,
        paramNames: ['decimals'],
      })

      assetData.decimal = assetDecimal
    }
  }

  return maybeRemoveDuplicates(preparedBalanceChanges)
}

async function tryToDecodeApprovalTransaction(transaction, asset) {
  if (!transaction?.data.startsWith(APPROVE_METHOD_ID)) return null

  const contract = SolidityContract.erc20(transaction.to)
  try {
    const decodedInput = contract.decodeInput(transaction.data)
    if (!decodedInput.values) return null

    const [grantedTo, balance] = decodedInput.values

    const symbolFromContract = await getAssetSymbolFromContract(transaction.to, asset)
    const symbol = symbolFromContract?.toUpperCase() || 'Unknown Token'

    return [{ grantedTo, balance, symbol, decimals: undefined }] // ToDo: Return 'decimals' in the future once we support changing the approval amount.
  } catch (e) {
    console.error(e.message)

    return null
  }
}

export async function retrieveSideEffects({ transaction, asset, shouldSimulate = true }) {
  assert(asset, 'retrieveSideEffects(): asset is required')

  const willSend = []
  const willReceive = []

  const approveTransactionData = await tryToDecodeApprovalTransaction(transaction, asset)
  if (approveTransactionData) {
    return { willApprove: approveTransactionData }
  }

  if (!shouldSimulate) {
    return null
  }

  if (!transaction.to) throw new Error(`'to' field is missing in the TX object`)

  const blocknativeTxObject = {
    to: transaction.to,
    input: transaction.data,
    from: transaction.from,
    value: ethHexToInt(transaction.value),
    gas: ethHexToInt(transaction.gas),
    maxFeePerGas: ethHexToInt(transaction.maxFeePerGas),
    maxPriorityFeePerGas: ethHexToInt(transaction.maxPriorityFeePerGas),
  }

  try {
    const simulatedTx = await fetchTxPreview(blocknativeTxObject)

    const [simulatedBalanceChanges] = simulatedTx.netBalanceChanges
    const [internalTransactions] = simulatedTx.internalTransactions

    const [sender] =
      simulatedBalanceChanges &&
      simulatedBalanceChanges.filter(({ address }) => address === transaction.from)

    if (sender) {
      const preparedBalanceChanges = await prepareBalanceChanges(
        internalTransactions,
        sender.balanceChanges,
        transaction.data,
        asset
      )

      for (const balanceChange of preparedBalanceChanges) {
        const { delta, asset: assetData } = balanceChange

        const account = {
          symbol: assetData.symbol,
          balance: delta,
          assetType: assetData.type,
          decimal: assetData.decimal,
        }

        if (delta.startsWith('-')) {
          willSend.push(account)
        } else {
          if (assetData.type === 'erc721') {
            account.nft = {
              title: `${assetData.title || assetData.symbol} #${delta}`,
            }
          }

          willReceive.push(account)
        }
      }
    }

    if (willSend.length === 0) {
      willSend.push({
        symbol: 'ETH',
        balance: `-${ethHexToInt(transaction.value)}`,
        assetType: 'ether',
        decimal: ethDecimals,
      })
    }
  } catch (err) {
    throw new Error(err)
  }

  return { willSend, willReceive }
}
